// ForkToChatButton — one-click handoff that loads a public Library
// playbook (currently any `guide` entry, or a `prompt` entry) into the
// Hub Chat panel as a primer + editable starter message.
//
// Why a separate button (not a fold inside ForkLibraryButton): "fork"
// in the rest of the app means *clone the resource into a squad/owner
// you control* — a real DB write that creates a new owned copy. This
// button doesn't write anything; it just stages a chat seed and
// navigates the user to /workspace?tab=chat. Conflating them would be
// confusing for both the UI and the eventual permission story.
import { useNavigate } from "react-router-dom";
import { MessageSquarePlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { stashChatSeed } from "@/lib/chatSeedBus";
import { bumpLibraryView } from "@/hooks/useLibrary";
import { toast } from "sonner";
import type { LibraryEntry } from "@/hooks/useLibrary";

// Loose subset of guide_data.steps[] — matches both the AI-generated
// shape (SharedGuide.GuideStep) and any future hand-authored variants.
interface GuideLikeStep {
  stepNumber?: number;
  title?: string;
  description?: string;
  examplePrompt?: string;
  proTip?: string;
  toolName?: string;
}

interface GuideLike {
  title?: string;
  description?: string;
  steps?: GuideLikeStep[];
  bonusTips?: string[];
}

interface PromptPayload {
  compiled?: string;
  task?: string;
  context?: string;
}

interface Props {
  entry: LibraryEntry;
  variant?: "default" | "outline" | "ghost";
  size?: "sm" | "default";
  className?: string;
}

/** Pull the guide_data blob, falling back to the payload root if it
 *  was stored flat (older seed data). */
const extractGuide = (entry: LibraryEntry): GuideLike | null => {
  if (entry.kind !== "guide") return null;
  const root = entry.payload as Record<string, unknown>;
  const candidate = (root.guide_data ?? root) as GuideLike;
  if (!candidate || !Array.isArray(candidate.steps) || candidate.steps.length === 0) {
    return null;
  }
  return candidate;
};

const extractPrompt = (entry: LibraryEntry): string | null => {
  if (entry.kind !== "prompt") return null;
  const p = entry.payload as PromptPayload;
  return (p.compiled || p.task || "")?.trim() || null;
};

/** Build the markdown primer. Kept compact — full step text + the
 *  example prompts so the model has actionable context, no fluff. */
const buildGuidePrimer = (entry: LibraryEntry, guide: GuideLike): string => {
  const lines: string[] = [];
  lines.push(`**Loaded playbook:** ${guide.title || entry.title}`);
  if (entry.summary || guide.description) {
    lines.push("");
    lines.push(entry.summary || guide.description || "");
  }
  lines.push("");
  lines.push("### Steps");
  guide.steps!.forEach((s, i) => {
    const n = s.stepNumber ?? i + 1;
    lines.push(`**${n}. ${s.title || "Untitled step"}**`);
    if (s.description) lines.push(s.description);
    if (s.toolName) lines.push(`_Tool:_ ${s.toolName}`);
    if (s.examplePrompt) {
      lines.push("");
      lines.push("```");
      lines.push(s.examplePrompt);
      lines.push("```");
    }
    if (s.proTip) lines.push(`> 💡 ${s.proTip}`);
    lines.push("");
  });
  if (guide.bonusTips && guide.bonusTips.length > 0) {
    lines.push("### Bonus tips");
    guide.bonusTips.forEach((t) => lines.push(`- ${t}`));
  }
  lines.push("");
  lines.push(
    "_I've loaded this playbook as context. Tell me your specific goal and I'll adapt the steps for you._",
  );
  return lines.join("\n");
};

const buildPromptPrimer = (entry: LibraryEntry, prompt: string): string =>
  [
    `**Loaded prompt:** ${entry.title}`,
    entry.summary ? `\n${entry.summary}` : "",
    "",
    "```",
    prompt,
    "```",
    "",
    "_Tell me how you want to adapt this — different audience, tighter constraints, a follow-up task — and I'll iterate with you._",
  ]
    .filter(Boolean)
    .join("\n");

const ForkToChatButton = ({
  entry,
  variant = "default",
  size = "sm",
  className,
}: Props) => {
  const navigate = useNavigate();

  const guide = extractGuide(entry);
  const promptText = extractPrompt(entry);

  // Only render when we have something chat-shaped to load. Other
  // entry kinds (jam, protocol_run, research) flow through the
  // existing Fork button.
  if (!guide && !promptText) return null;

  const handleClick = () => {
    const primer = guide
      ? buildGuidePrimer(entry, guide)
      : buildPromptPrimer(entry, promptText!);

    const draft = guide
      ? `Walk me through step 1 for my goal: `
      : `Adapt this prompt for: `;

    stashChatSeed({
      primer,
      draft,
      source: "library:fork-to-chat",
      entryId: entry.id,
      title: entry.title,
    });

    // View bump matches the click-through behaviour of the card's
    // main title link, so the entry's view_count reflects the seed.
    // Guard with a UUID check so synthetic entries (used by the
    // SharedGuide page, which doesn't load from `library_entries`)
    // skip the no-op RPC call.
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(entry.id)) {
      bumpLibraryView(entry.id);
    }

    toast.success("Loaded into chat — continue with your own goal");
    navigate("/workspace?tab=chat");
  };

  return (
    <Button
      type="button"
      variant={variant}
      size={size}
      onClick={handleClick}
      className={cn("font-mono", className)}
      title="Load this playbook into the Hub chat and continue with your own goal"
    >
      <MessageSquarePlus className="w-3.5 h-3.5 mr-1.5" />
      Fork to chat
    </Button>
  );
};

export default ForkToChatButton;
